
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { LeadImage } from '@/types/lead';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { setPrimaryImage, deleteLeadImage } from '@/services/leadImagesService';
import { toast } from '@/hooks/use-toast';

interface ImageGalleryProps {
  images: LeadImage[];
  leadId: string;
  onImageUpdate?: () => void;
  className?: string;
}

interface ThumbnailGridProps {
  images: LeadImage[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  leadId: string;
  onImageUpdate?: () => void;
}

const ThumbnailGrid: React.FC<ThumbnailGridProps> = ({ 
  images, 
  selectedIndex, 
  onSelect, 
  leadId,
  onImageUpdate 
}) => {
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const handleSetPrimary = async (imageId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(prev => ({ ...prev, [imageId]: true }));
    try {
      const success = await setPrimaryImage(imageId, leadId);
      if (success) {
        toast({
          title: "Primary Image Set",
          description: "The image has been set as the primary image"
        });
      }
      if (onImageUpdate) onImageUpdate();
    } finally {
      setLoading(prev => ({ ...prev, [imageId]: false }));
    }
  };

  const handleDelete = async (imageId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this image?')) {
      setLoading(prev => ({ ...prev, [imageId]: true }));
      try {
        const success = await deleteLeadImage(imageId, leadId);
        if (success) {
          toast({
            title: "Image Deleted",
            description: "The image has been deleted successfully"
          });
        }
        if (onImageUpdate) onImageUpdate();
      } finally {
        setLoading(prev => ({ ...prev, [imageId]: false }));
      }
    }
  };

  return (
    <div className="grid grid-cols-5 gap-2 mt-2">
      {images.map((image, index) => (
        <div
          key={image.id || index}
          onClick={() => onSelect(index)}
          className={cn(
            "relative cursor-pointer rounded-md overflow-hidden border-2 h-16 w-16",
            selectedIndex === index ? "border-primary" : "border-transparent",
            loading[image.id] && "opacity-50"
          )}
        >
          <img 
            src={image.imageUrl} 
            alt={`Image ${index + 1}`}
            className="h-full w-full object-cover"
          />
          {image.isPrimary && (
            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
              <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                ✓
              </span>
            </div>
          )}
          {!loading[image.id] && (
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 rounded-full bg-white text-black"
                onClick={(e) => handleSetPrimary(image.id, e)}
                title="Set as primary image"
              >
                {image.isPrimary ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <span className="text-xs">★</span>
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 rounded-full bg-white text-black ml-1"
                onClick={(e) => handleDelete(image.id, e)}
                title="Delete image"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          {loading[image.id] && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  images, 
  leadId, 
  onImageUpdate,
  className
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // If no images, display placeholder
  if (!images || images.length === 0) {
    return (
      <Card className={cn("overflow-hidden relative", className)}>
        <div className="aspect-video bg-muted flex items-center justify-center">
          <p className="text-muted-foreground">No images available</p>
        </div>
      </Card>
    );
  }

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setImageLoaded(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setImageLoaded(false);
  };

  const openFullscreen = () => {
    setFullscreenOpen(true);
  };

  return (
    <>
      <Card className={cn("overflow-hidden relative", className)}>
        <div 
          className="aspect-video cursor-pointer" 
          onClick={openFullscreen}
        >
          {!imageLoaded && (
            <Skeleton className="w-full h-full absolute inset-0" />
          )}
          <img
            src={images[currentIndex].imageUrl}
            alt={`Lead image ${currentIndex + 1}`}
            className={cn(
              "w-full h-full object-cover",
              !imageLoaded && "opacity-0"
            )}
            onLoad={() => setImageLoaded(true)}
          />
        </div>
        
        {images.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70"
              onClick={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white hover:bg-black/70"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
            
            <div className="p-2">
              <ThumbnailGrid 
                images={images} 
                selectedIndex={currentIndex} 
                onSelect={setCurrentIndex} 
                leadId={leadId}
                onImageUpdate={onImageUpdate}
              />
            </div>
          </>
        )}
      </Card>

      <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
        <DialogContent className="max-w-6xl w-full">
          <Carousel className="w-full">
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={image.id || index}>
                  <div className="relative h-[80vh] flex items-center justify-center">
                    <img
                      src={image.imageUrl}
                      alt={`Lead image ${index + 1}`}
                      className="max-h-full max-w-full object-contain"
                    />
                    {image.isPrimary && (
                      <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded-md text-sm flex items-center">
                        <Check className="h-4 w-4 mr-1" />
                        Primary Image
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-4" />
            <CarouselNext className="right-4" />
          </Carousel>
          
          <Button 
            className="absolute top-4 right-4 rounded-full"
            size="icon"
            variant="ghost"
            onClick={() => setFullscreenOpen(false)}
          >
            <X className="h-6 w-6" />
          </Button>
          
          <div className="p-2">
            <ThumbnailGrid 
              images={images} 
              selectedIndex={currentIndex} 
              onSelect={setCurrentIndex}
              leadId={leadId}
              onImageUpdate={onImageUpdate} 
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageGallery;
